import { useCallback } from 'react';

/**
 * Gets the default variant by checking if its specified already in contentstack
 * settings, otherwise it will try to find a variant with "Queen" in the options.
 * @param productData The product data from Contentstack
 * @param defaultVariantOverrideSetting Also from CS and is formatted as a
 *   variant title (e.g. "Queen / Light Grey").
 */
export function getDefaultVariant(productData, defaultVariantOverrideSetting) {
  const shopifyProduct = productData?.shopifyProduct;
  const variants = shopifyProduct.variants;

  let defaultVariant;

  if (defaultVariantOverrideSetting) {
    const overrideOptions = defaultVariantOverrideSetting
      .split(' / ')
      .filter(Boolean);

    defaultVariant = variants.find((variant) =>
      overrideOptions.every((option) =>
        variant.selectedOptions.some((selected) => selected.value === option)
      )
    );
  }

  // Fallback: try to find a variant with "Queen", or default to the first variant.
  if (!defaultVariant) {
    defaultVariant =
      variants.find((v) =>
        v.selectedOptions.find((o) => o.value === 'Queen')
      ) ?? variants[0];
  }

  return defaultVariant;
}

/**
 * Finds the variant that matches a provided set of option values.
 *
 * Unlike `calculateNewVariant`, this returns `null` when no exact match exists
 * instead of falling back to the first variant.
 */
export function findMatchingVariant(shopifyProduct, selectedOptions) {
  const relevantOptions = Object.entries(selectedOptions ?? {}).filter(
    ([, value]) => value != null
  );

  if (!relevantOptions.length) {
    return null;
  }

  return (
    shopifyProduct?.variants.find((variant) =>
      relevantOptions.every(([key, value]) =>
        variant.selectedOptions.some(
          (option) =>
            option.name.toLowerCase() === key && option.value === value
        )
      )
    ) ?? null
  );
}

/**
 * Whether a product has anything worth showing a variant selector for. Shopify
 * always returns at least one option, so a product with no real variants shows
 * up as a single option holding a single value.
 */
export function hasSelectableOptions(options) {
  return options.length > 1 || options[0]?.values.length > 1;
}

/**
 * Builds the choices a variant selector renders for one option, keeping only
 * the values that resolve to a real variant against the current selection.
 * Out-of-stock values are kept, but labelled.
 */
export function buildOptionChoices({
  shopifyProduct,
  selectedOptions,
  optionName,
  optionValues
}) {
  return optionValues
    .map((value) => {
      const matchingVariant = findMatchingVariant(shopifyProduct, {
        ...selectedOptions,
        [optionName]: value
      });

      if (!matchingVariant) {
        return null;
      }

      return {
        value,
        label: matchingVariant.outOfStock ? `${value} (Out of Stock)` : value
      };
    })
    .filter(Boolean);
}

/**
 * Figures out the new variant based on the selected options.
 */
export function calculateNewVariant(shopifyProduct, newState) {
  return (
    shopifyProduct.variants.find((variant) =>
      variant.selectedOptions.every(
        (option) => option.value === newState[option.name.toLowerCase()]
      )
    ) ?? shopifyProduct.variants[0]
  );
}

/**
 * Formats the selected options into { size: value, color: value, ... }
 * if necessary. The main use case is to convert an array of selectedOptions
 * from Shopify ([{ name: 'Size', value: 'Queen' }, ...]) into an object.
 * @param options Shopify selectedOptions array or an object
 * @returns An object with options spread with { name: value }
 */
export function normalizeOptionsObj(options) {
  return Array.isArray(options)
    ? options.reduce((acc, { name, value }) => {
        acc[name.toLowerCase()] = value;
        return acc;
      }, {})
    : options;
}

/**
 * Flattens the cross_sell block into the add-on products offered on this pdp.
 */
export function getCrossSellProducts(productData) {
  return productData?.product_detail_blocks.flatMap((block) => {
    if (Object.keys(block)[0] !== 'cross_sell') return [];

    return (block.cross_sell?.reference[0]?.products ?? []).map((product) => ({
      ...product?.reference[0]?.shopifyProduct[0],
      singleSize: product?.single_size_product || false,
      matchSecondaryAttr: product?.match_secondary_attribute || false,
      secondaryAttrOverride: product?.secondary_attribute_override || null
    }));
  });
}

/**
 * Hook that lets you update URL params with an object of key-value pairs.
 * Important: The callback returned does NOT seem to queue or batch updates. If
 * you need to update multiple params in quick succession, you should do so in a
 * single call.
 *
 * @returns A callback to update the URL with the new state.
 * This callback accepts:
 * @param state - the new state to update the URL with { key: value }
 * @param method - the method to use to update the URL, either 'replace' or
 *   'push' (default: 'replace')
 */
export function useUpdateURL() {
  return useCallback((state, method = 'replace') => {
    const newParams = new URLSearchParams(window.location.search);
    Object.entries(state).forEach(([key, value]) => {
      newParams.set(key, value);
    });
    const url = `?${newParams.toString()}`;

    // Nextjs docs actually say we can use the native History API now for this
    // purpose instead of useRouter. It's much faster and perfect for this case.
    if (method === 'replace') {
      window.history.replaceState(null, '', url);
    } else if (method === 'push') {
      window.history.pushState(null, '', url);
    } else {
      throw new Error(`Invalid method: ${method}. Use "replace" or "push".`);
    }
  }, []);
}
